import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useCategories = () => {
    const [categories, setCategories] = useState(null);
    const [isUpdatedCategories, setIsUpdatedCategories] = useState(false);
    const [HideCategoriesSkeleton, setHideCategoriesSkeleton] = useState(false);
    const [categoriesStoreUsername, setCategoriesStoreUsername] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedCategories: false,
        categoriesStoreUsername: '',
        categories: null,
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedCategories,
        categoriesStoreUsername,
        categories,
    };

    const updateCategories = (newCategories) => {
        setCategories(newCategories);
        setIsUpdatedCategories(true);
    };

    const isEmptyItems = useCallback((categoriesData) => {
        for (const category of categoriesData) {
            if (category.items.length > 0) {
                return false;
            }
        }
        return true;
    }, []);

    const fetchCategories = useCallback(async (store, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: store,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedCategories && cache.categoriesStoreUsername === store
            ),
            getCachedValue: (cache) => cache.categories,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/menu/categories/${store}`
                    );
                    clearError();
                    updateCategories(responseData.menu);
                    setCategoriesStoreUsername(store);
                    return responseData;
                } catch (err) {
                    setIsUpdatedCategories(true);
                    setCategoriesStoreUsername(store);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const CategoryDeletedHandler = useCallback(deletedCategoryId => {
        setCategories(prevCategories => {
            return {
                ...prevCategories,
                categories: prevCategories.categories.filter(category => category.id !== deletedCategoryId)
            };
        });
    }, [setCategories]);

    const ProductDeletedHandler = useCallback((deletedProductId, categoryIndex) => {
        setCategories(prevProducts => {
            const updatedCategories = [...prevProducts.categories];
            const updatedItems = [...updatedCategories[categoryIndex]?.items];

            const filteredItems = updatedItems.filter(product => product.id !== deletedProductId);

            updatedCategories[categoryIndex] = {
                ...updatedCategories[categoryIndex],
                items: filteredItems
            };

            return {
                ...prevProducts,
                categories: updatedCategories
            };
        });
    }, [setCategories]);

    const reloadCategories = useCallback(
        (store) => fetchCategories(store, { force: true }),
        [fetchCategories]
    );

    const removeCategories = useCallback(() => {
        pendingFetchesRef.current.clear();
        setCategories(null);
        setCategoriesStoreUsername('');
        setIsUpdatedCategories(false);
        setHideCategoriesSkeleton(false);
        clearError();
    }, [clearError]);

    useEffect(() => {
        removeCategories();
    }, []);

    return {
        categories,
        updateCategories,
        isUpdatedCategories,
        fetchCategories,
        reloadCategories,
        HideCategoriesSkeleton,
        isEmptyItems,
        CategoryDeletedHandler,
        ProductDeletedHandler,
        categoriesError: error,
        setHideCategoriesSkeleton,
        removeCategories
    };
}

export default useCategories;
